const cities = [
  { name: "Plantation", zips: "33317, 33322, 33324, 33325" },
  { name: "Lauderhill", zips: "33313, 33319" },
  { name: "Sunrise", zips: "33313, 33322, 33323, 33351" },
  { name: "Davie", zips: "33314, 33317, 33324, 33328, 33330" },
  { name: "Cooper City", zips: "33328, 33330, 33024" },
  { name: "Weston", zips: "33326, 33327, 33331, 33332" },
  { name: "Fort Lauderdale", zips: "33301-33316" },
  { name: "Pembroke Pines", zips: "33023-33029" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or work in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function PlantationLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Plantation &amp; Broward County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Plantation, Sunrise, Davie, Lauderhill, Cooper City, or anywhere in central Broward County, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Plantation carries the nickname &quot;The City of Trees&quot; with genuine justification — this master-planned community was designed from its inception in 1953 by developer Frederick Peters to preserve Florida&apos;s natural canopy, and those Jacaranda-lined boulevards remain one of the most distinctive visual signatures in Broward County. Unlike the dense urban corridor of Fort Lauderdale to the east or the sprawl of western Broward, Plantation occupies a sweet spot: a large, well-planned suburban city with strong schools, diverse neighborhoods, and immediate access to virtually every major transportation artery in South Florida.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city&apos;s geography is a travel advantage that Plantation residents often overlook. Fort Lauderdale-Hollywood International Airport sits roughly 15–20 minutes south via I-595 or Florida&apos;s Turnpike, making it one of the most conveniently located suburbs for air travel in all of South Florida. Port Everglades — the world&apos;s third-busiest cruise port — is equally accessible for families planning Caribbean sailings. Sawgrass Mills, one of the largest outlet malls in the United States and a genuine international shopping destination that draws visitors from Latin America and the Caribbean, sits at Plantation&apos;s western edge. The Broward Mall and the Shops at Westfield Broward anchor the city&apos;s retail corridor along Broward Boulevard, while Central Park and Plantation Heritage Park provide the green spaces that give the city its character.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Plantation&apos;s population of roughly 95,000 residents is notably diverse and well-educated. The city has a significant Caribbean community, a growing Latin American population, and a established Jewish community that contributes to the cultural fabric of central Broward. American Heritage School — one of the top private schools in Florida — draws families from across the county and beyond, and the proximity to Nova Southeastern University in Davie means that graduate students, medical residents, and researchers are part of the local demographic mix. South Plantation High School and Plantation High School both serve large, diverse student bodies with active international programs, exchange partnerships, and study-abroad opportunities that create regular passport demand among families.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city&apos;s housing stock reflects its master-planned origins: established neighborhoods with mature landscaping, newer developments near the western edge, and the gated communities that dot the landscape between Broward Boulevard and State Road 84. Plantation Woods, Jacaranda Lakes, and The Hills are among the well-known residential areas, while newer construction near the Sawgrass corridor has brought additional density and a younger demographic to the city&apos;s western reaches. The combination of established affluence and newer families creates a travel profile that blends Caribbean cruises, Latin America business trips, European vacations, and educational travel.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What defines Plantation travel patterns is accessibility. Residents are not committed to a single airport — Fort Lauderdale-Hollywood International is the default, but Miami International is 35–40 minutes south via the Turnpike, and Palm Beach International is 45 minutes north via I-95. This flexibility means that Plantation travelers often find the best flight deals across multiple airports, but it also means that passport emergencies can arise from bookings at any of the three. A family that finds a last-minute cruise deal departing Port Everglades, a medical resident at Nova Southeastern who gets a conference acceptance in Barcelona, or a business owner who closes a deal requiring a trip to Bogota — all of these scenarios play out regularly in Plantation, and all of them require current passports on short notice.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Plantation residents across every neighborhood and travel scenario. We understand that a family in Jacaranda Lakes preparing for a Caribbean cruise faces the same urgency as a graduate student at Nova Southeastern flying to a research conference in London. Our courier-based expedited service means you start online from your home near Central Park, your condo near Sawgrass Mills, or your office on Broward Boulevard — and your passport arrives within days, not months. The closest Fast Passport Center office is in Delray Beach, approximately 30 miles north via I-95, with courier pickup available throughout central Broward County.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Plantation — approximately 30 miles north via I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout central Broward County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location near Sawgrass Mills or Broward Mall, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in central Broward County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Fort Lauderdale", href: "/cities/fort-lauderdale" },
                  { name: "Weston", href: "/cities/weston" },
                  { name: "Aventura", href: "/cities/aventura" },
                  { name: "Boca Raton", href: "/cities/boca-raton" },
                  { name: "Delray Beach", href: "/cities/delray-beach" },
                  { name: "Coral Springs", href: "/cities/coral-springs" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}